/**
 * @file BlogHeroImage.tsx
 * @summary Hero image + helpers SEO/GEO pour les articles de blog hors ArticleLayout.
 *
 * Les articles TSX qui n'utilisent pas ArticleLayout doivent quand même exposer
 * au moins une image indexable pour les SERP Google/Bing. Ce composant rend la
 * figure visible (alt, title, caption, width/height) et l'helper `withArticleImages`
 * injecte le tableau `image[]` dans le JSON-LD Article correspondant.
 */
import { getBlogImagesForSlug, slugFromUrl, absoluteImageUrl } from "@/lib/blog-images";

/** Hero image visible — assure que Google associe une image à l'URL de l'article. */
export default function BlogHeroImage({ slug }: { slug: string }) {
  const img = getBlogImagesForSlug(slug, 1)[0];
  if (!img) return null;
  return (
    <figure className="mb-8 -mx-4 md:mx-0">
      <img
        src={img.path}
        alt={img.alt}
        title={img.title}
        width={img.width}
        height={img.height}
        loading="eager"
        fetchPriority="high"
        decoding="async"
        className="w-full h-auto md:rounded-lg border border-border/40 object-cover max-h-[520px]"
      />
      <figcaption className="mt-2 px-4 md:px-0 text-xs text-muted-foreground italic">
        {img.caption}
      </figcaption>
    </figure>
  );
}

/** Récupère la liste d'URLs absolues d'images pour un slug (jusqu'à 5). */
export function getArticleImageUrls(slug: string, count = 5): string[] {
  return getBlogImagesForSlug(slug, count).map(absoluteImageUrl);
}

/**
 * Injecte `image[]` dans toute entrée JSON-LD de type Article qui n'en a pas.
 * Préserve les autres types (FAQPage, LocalBusiness, etc.).
 */
export function withArticleImages(jsonLd: object | object[], images: string[]): object[] {
  const arr = Array.isArray(jsonLd) ? [...jsonLd] : [jsonLd];
  return arr.map((node: any) => {
    if (!node || typeof node !== "object") return node;
    const t = node["@type"];
    const isArticle = t === "Article" || (Array.isArray(t) && t.includes("Article"));
    if (!isArticle || node.image) return node;
    return { ...node, image: images };
  });
}

export { slugFromUrl };
